import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

import { useDevice } from "../../../../Api/newSockets.api";
import DeviceMenu from "../../Shared/DeviceMenu";
import DevicesHeader from "../../../../components/Header/DevicesHeader";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { savePathToLocalStorage } from "../../../../utils/helpers";
import PointCloudViewer from "../../../../components/ThreeD/PointCloudViewer";

type Props = {
  match: any;
  deviceId: string;
  label: string;
  icon: any;
};

const ACCENT = "#673ab7";

/** Normalize a socket.io binary payload (ArrayBuffer | typed array | null) to ArrayBuffer. */
function toArrayBuffer(v: any): ArrayBuffer | null {
  if (!v) return null;
  if (v instanceof ArrayBuffer) return v;
  if (ArrayBuffer.isView(v)) {
    // Copy out the exact byte range into a fresh ArrayBuffer (handles shared or
    // offset backing buffers).
    const view = v as ArrayBufferView;
    const out = new Uint8Array(view.byteLength);
    out.set(new Uint8Array(view.buffer as ArrayBuffer, view.byteOffset, view.byteLength));
    return out.buffer;
  }
  return null;
}

/** ArrayBuffer -> base64 (only used on confirm/save, where Hive expects base64). */
function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any);
  }
  return btoa(binary);
}

const Stream = (props: Props) => {
  savePathToLocalStorage();

  const [streamImg, setStreamImg] = useState(
    "https://upload.wikimedia.org/wikipedia/commons/b/b1/Loading_icon.gif?20151024034921"
  );

  const deviceId = props.match.params.deviceId;

  const [captureModalOpen, setCaptureModalOpen] = useState(false);
  const [captureName, setCaptureName] = useState("");
  const [captureLoading, setCaptureLoading] = useState(false);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  // .ply data now arrives as a raw ArrayBuffer (binary over socket.io) rather
  // than base64, so it can be fed straight into PLYLoader.
  const [scenePly, setScenePly] = useState<ArrayBuffer | null>(null);
  const [objectPly, setObjectPly] = useState<ArrayBuffer | null>(null);
  const [capturedMetrics, setCapturedMetrics] = useState<any>({});
  const [previewVariant, setPreviewVariant] = useState<"scene" | "object">("scene");
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Object render mode: points, or a textured surface mesh (depth/smooth).
  const [objectMode, setObjectMode] = useState<"points" | "depth" | "smooth">("points");
  const [meshPly, setMeshPly] = useState<ArrayBuffer | null>(null);
  const [meshLoading, setMeshLoading] = useState(false);
  const [meshInfo, setMeshInfo] = useState<{ vertices?: number; faces?: number } | null>(null);

  // Progressive full-res object streaming state.
  const [objectFullPoints, setObjectFullPoints] = useState(0); // total pts to stream
  const [streamProgress, setStreamProgress] = useState<number | null>(null); // 0..1 | null
  const [streamGeom, setStreamGeom] = useState<THREE.BufferGeometry | null>(null);
  const [streamVersion, setStreamVersion] = useState(0);
  const streamState = useRef<{
    geom: THREE.BufferGeometry | null;
    pos: Float32Array | null;
    col: Float32Array | null;
    filled: number;
    total: number;
    cleanup?: () => void;
  }>({ geom: null, pos: null, col: null, filled: 0, total: 0 });

  const { getDevice, stereoCapture, confirmStereoCapture, streamStereoObject, stereoMeshObject } = useDevice();

  const device = getDevice(parseInt(deviceId));
  const deviceData = device.device;
  const settings = deviceData?.settings ?? {};

  useEffect(() => {
    if (device.device?.settings?.cameraStreamEndpoint) {
      setStreamImg(device.device.settings.cameraStreamEndpoint + `?cb=${Math.random()}`);
    }
  }, [device.device]);

  const handleCapture = async () => {
    if (captureLoading) return;
    setCaptureLoading(true);
    try {
      const response: any = await stereoCapture(
        deviceId,
        settings?.defaultDataset ?? null,
        captureName || "untitled"
      );
      const sceneBuf = toArrayBuffer(response?.scenePly);
      if (response && sceneBuf) {
        resetStream();
        resetMesh();
        setScenePly(sceneBuf);
        setObjectPly(toArrayBuffer(response.objectPly));
        setCapturedMetrics(response.metrics ?? {});
        // How many full-res object points are available to stream on demand.
        setObjectFullPoints(response.metrics?.object_full_points ?? 0);
        setPreviewVariant("scene");
        setCaptureModalOpen(false);
        setCaptureLoading(false);
        setPreviewModalOpen(true);
      } else {
        console.error("Invalid stereo capture response:", response);
        alert("Failed to capture 3D model. Please try again.");
        setCaptureLoading(false);
      }
    } catch (error) {
      console.error("Stereo capture error:", error);
      alert("Failed to capture 3D model. Please try again.");
      setCaptureLoading(false);
    }
  };

  // ---- Progressive full-res object streaming --------------------------------
  const resetStream = () => {
    streamState.current.cleanup?.();
    streamState.current = { geom: null, pos: null, col: null, filled: 0, total: 0 };
    setStreamGeom(null);
    setStreamProgress(null);
    setStreamVersion(0);
  };

  const startObjectStream = () => {
    if (!objectFullPoints || objectFullPoints <= 0) return;
    if (streamProgress != null) return; // already streaming/streamed

    // Pre-allocate buffers sized to the total point count; fill as chunks arrive.
    const total = objectFullPoints;
    const pos = new Float32Array(total * 3);
    const col = new Float32Array(total * 3);
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geom.setAttribute("color", new THREE.BufferAttribute(col, 3));
    geom.setDrawRange(0, 0);
    streamState.current = { geom, pos, col, filled: 0, total };
    setStreamGeom(geom);
    setStreamProgress(0);

    const cleanup = streamStereoObject(deviceId, {
      onChunk: (evt: any) => {
        const st = streamState.current;
        if (!st.geom || !st.pos || !st.col) return;
        const buf = toArrayBuffer(evt.data);
        if (!buf) return;
        // Chunk layout: per vertex float32 x,y,z (12B) + uint8 r,g,b (3B) = 15B.
        const stride = evt.stride ?? 15;
        const count = evt.chunkPoints ?? Math.floor(buf.byteLength / stride);
        const dv = new DataView(buf);
        let base = st.filled;
        for (let i = 0; i < count; i++) {
          const o = i * stride;
          const p = (base + i) * 3;
          st.pos[p] = dv.getFloat32(o, true);
          st.pos[p + 1] = dv.getFloat32(o + 4, true);
          st.pos[p + 2] = dv.getFloat32(o + 8, true);
          st.col[p] = dv.getUint8(o + 12) / 255;
          st.col[p + 1] = dv.getUint8(o + 13) / 255;
          st.col[p + 2] = dv.getUint8(o + 14) / 255;
        }
        st.filled += count;
        st.geom.setDrawRange(0, st.filled);
        (st.geom.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
        (st.geom.getAttribute("color") as THREE.BufferAttribute).needsUpdate = true;
        setStreamVersion((v) => v + 1);
        setStreamProgress(st.total > 0 ? Math.min(st.filled / st.total, 1) : 1);
      },
      onDone: () => {
        const st = streamState.current;
        if (st.geom) {
          st.geom.setDrawRange(0, st.filled);
          st.geom.computeBoundingBox();
          st.geom.computeBoundingSphere();
        }
        setStreamProgress(1);
        setStreamVersion((v) => v + 1);
      },
      onError: (err: string) => {
        console.error("Object stream error:", err);
        resetStream();
      },
    });
    streamState.current.cleanup = cleanup;
  };

  // Clean up any in-flight stream if the modal closes / component unmounts.
  useEffect(() => {
    return () => streamState.current.cleanup?.();
  }, []);

  // ---- Object surface mesh (solid textured 3D-model view) ------------------
  const selectObjectMode = async (mode: "points" | "depth" | "smooth") => {
    setObjectMode(mode);
    if (mode === "points") return;
    // Build (or rebuild) the mesh on the device for this method.
    setMeshLoading(true);
    setMeshPly(null);
    setMeshInfo(null);
    try {
      const res: any = await stereoMeshObject(deviceId, mode);
      if (res && res.ok) {
        const buf = toArrayBuffer(res.mesh);
        setMeshPly(buf);
        setMeshInfo({ vertices: res.vertices, faces: res.faces });
      } else {
        alert(`Failed to build ${mode} mesh: ${res?.error || "unknown error"}`);
        setObjectMode("points");
      }
    } catch (e) {
      console.error("mesh build error", e);
      setObjectMode("points");
    } finally {
      setMeshLoading(false);
    }
  };

  const resetMesh = () => {
    setObjectMode("points");
    setMeshPly(null);
    setMeshInfo(null);
    setMeshLoading(false);
  };

  const activePly = previewVariant === "object" && objectPly ? objectPly : scenePly;
  // When viewing the object and a stream is active/complete, show the streamed
  // full-res geometry; otherwise fall back to the instant preview .ply.
  const showStreamGeom = previewVariant === "object" && streamGeom != null;
  // Show the solid textured mesh when the object is in a mesh mode and it loaded.
  const showMesh = previewVariant === "object" && objectMode !== "points" && meshPly != null;

  return deviceData ? (
    <>
      <div className="device-main main-content">
        <DevicesHeader device={deviceData} />
        <div className="stream-body-content">
          <div>
            <DeviceMenu typeId={deviceData.deviceTypes.id} />
          </div>
          <div className="stream-content">
            <div className="stream-controls">
              <div className="control-group">
                <button
                  onClick={() => {
                    setCaptureName("");
                    setCaptureModalOpen(true);
                  }}
                  className="control-btn primary"
                  title="Capture 3D model"
                  style={{ borderColor: ACCENT, color: ACCENT }}
                >
                  <span>🧊 Capture 3D</span>
                </button>
              </div>
            </div>

            {/* Live color stream (same MJPEG mechanism as a normal camera) */}
            <div className="stream-view">
              <img src={streamImg} alt="Stereo camera color stream" />
            </div>
          </div>
        </div>
      </div>

      {/* Capture Name Modal */}
      {captureModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div
            style={{
              background: "#21282c",
              border: `2px solid ${ACCENT}`,
              borderRadius: 16,
              padding: "2rem",
              maxWidth: 480,
              width: "100%",
              boxShadow: `0 0 60px rgba(103, 58, 183, 0.4)`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <div style={{ fontSize: "2rem", background: "rgba(103, 58, 183, 0.2)", padding: "0.5rem", borderRadius: 8, border: "1px solid rgba(103, 58, 183, 0.4)" }}>🧊</div>
                <div>
                  <h2 style={{ margin: 0, color: ACCENT, fontSize: "1.5rem", fontWeight: "bold", letterSpacing: 1 }}>
                    {captureLoading ? "CAPTURING 3D..." : "CAPTURE 3D MODEL"}
                  </h2>
                  <p style={{ margin: "0.25rem 0 0 0", color: "#6c757d", fontSize: "0.85rem", fontFamily: "monospace" }}>
                    {captureLoading ? "Reconstructing point cloud..." : `${deviceData?.name ?? "Stereo Camera"} • Device ${deviceId}`}
                  </p>
                </div>
              </div>
              <button
                disabled={captureLoading}
                onClick={() => !captureLoading && setCaptureModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "2px solid #dc3545",
                  borderRadius: 8,
                  color: "#dc3545",
                  fontSize: "1.5rem",
                  width: 40,
                  height: 40,
                  cursor: captureLoading ? "not-allowed" : "pointer",
                  opacity: captureLoading ? 0.5 : 1,
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: "1.5rem", display: captureLoading ? "none" : "block" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", color: ACCENT, fontSize: "0.9rem", fontWeight: "bold", textTransform: "uppercase" }}>
                Capture Name
              </label>
              <input
                type="text"
                value={captureName}
                onChange={(e) => setCaptureName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !captureLoading) handleCapture(); }}
                placeholder="Enter a name for this capture..."
                disabled={captureLoading}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  background: "#242424",
                  border: "2px solid rgba(103, 58, 183, 0.3)",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: "1rem",
                  fontFamily: "monospace",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                autoFocus
              />
            </div>

            {captureLoading && (
              <div style={{ display: "flex", justifyContent: "center", padding: "1rem 0" }}>
                <LoadingSpinner />
              </div>
            )}

            <div style={{ display: captureLoading ? "none" : "flex", gap: "1rem", justifyContent: "flex-end" }}>
              <button
                onClick={() => { setCaptureModalOpen(false); setCaptureName(""); }}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "transparent",
                  border: "2px solid #6c757d",
                  borderRadius: 8,
                  color: "#6c757d",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCapture}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "linear-gradient(135deg, rgba(103, 58, 183, 0.4) 0%, rgba(103, 58, 183, 0.2) 100%)",
                  border: `2px solid ${ACCENT}`,
                  borderRadius: 8,
                  color: ACCENT,
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  boxShadow: "0 0 20px rgba(103, 58, 183, 0.4)",
                }}
              >
                🧊 CAPTURE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3D Preview Modal */}
      {previewModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div
            style={{
              background: "#21282c",
              border: `2px solid ${ACCENT}`,
              borderRadius: 16,
              padding: "1.5rem",
              maxWidth: 760,
              width: "100%",
              boxShadow: "0 0 60px rgba(103, 58, 183, 0.4)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <h2 style={{ margin: 0, color: ACCENT, fontSize: "1.3rem", fontWeight: "bold", letterSpacing: 1 }}>
                3D CAPTURE PREVIEW
              </h2>
              {/* Scene / Object toggle */}
              <div style={{ display: "flex", gap: 8 }}>
                {(["scene", "object"] as const).map((v) => {
                  const disabled = v === "object" && !objectPly;
                  const active = previewVariant === v;
                  return (
                    <button
                      key={v}
                      disabled={disabled}
                      onClick={() => { setPreviewVariant(v); if (v === "scene") resetMesh(); }}
                      title={disabled ? "No object was isolated from this capture" : ""}
                      style={{
                        padding: "0.4rem 1rem",
                        borderRadius: 8,
                        border: `2px solid ${active ? ACCENT : "#444"}`,
                        background: active ? "rgba(103, 58, 183, 0.25)" : "transparent",
                        color: disabled ? "#555" : active ? ACCENT : "#aaa",
                        fontWeight: "bold",
                        textTransform: "capitalize",
                        cursor: disabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>

            <PointCloudViewer
              data={showMesh ? meshPly : showStreamGeom ? null : activePly}
              geometryOverride={showMesh ? null : showStreamGeom ? streamGeom : null}
              geometryVersion={streamVersion}
              renderAsMesh={showMesh}
              progress={showMesh || !showStreamGeom ? null : streamProgress}
              progressLabel={
                !showMesh && showStreamGeom && streamProgress != null
                  ? `${Math.round((streamState.current.filled || 0)).toLocaleString()} / ${objectFullPoints.toLocaleString()} pts`
                  : undefined
              }
              height={440}
              label={previewVariant === "object" ? "OBJECT" : "SCENE"}
            />

            {/* Object render-mode controls (points / solid mesh / smooth mesh) */}
            {previewVariant === "object" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "0.6rem", flexWrap: "wrap" }}>
                <span style={{ color: "#8a8a8a", font: "600 10px/1 monospace", letterSpacing: 1, textTransform: "uppercase" }}>Render</span>
                {([
                  { k: "points", label: "◦ Points" },
                  { k: "depth", label: "◆ Solid" },
                  { k: "smooth", label: "● Smooth" },
                ] as const).map(({ k, label }) => {
                  const active = objectMode === k;
                  return (
                    <button
                      key={k}
                      disabled={meshLoading}
                      onClick={() => selectObjectMode(k)}
                      style={{
                        padding: "0.35rem 0.9rem",
                        borderRadius: 20,
                        border: `1px solid ${active ? "#00e5ff" : "rgba(103,58,183,0.5)"}`,
                        background: active ? "rgba(0,229,255,0.15)" : "rgba(103,58,183,0.1)",
                        color: active ? "#00e5ff" : "#b39ddb",
                        font: "600 11px/1 monospace",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        cursor: meshLoading ? "wait" : "pointer",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
                {meshLoading && <span style={{ color: "#00e5ff", font: "600 11px/1 monospace" }}>building mesh…</span>}
                {!meshLoading && meshInfo && objectMode !== "points" && (
                  <span style={{ color: "#777", font: "600 10px/1 monospace" }}>
                    {meshInfo.vertices?.toLocaleString()} verts · {meshInfo.faces?.toLocaleString()} faces
                  </span>
                )}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "1rem", margin: "0.75rem 0 0" }}>
              {capturedMetrics && capturedMetrics.has_object !== undefined && (
                <p style={{ color: "#888", fontFamily: "monospace", fontSize: 12, margin: 0 }}>
                  Object isolated: {capturedMetrics.has_object ? "yes" : "no"}
                </p>
              )}
              {/* Progressive full-detail streaming (object only) */}
              {previewVariant === "object" && objectFullPoints > 0 && (
                <button
                  disabled={streamProgress != null}
                  onClick={startObjectStream}
                  title={`Stream all ${objectFullPoints.toLocaleString()} points`}
                  style={{
                    marginLeft: "auto",
                    padding: "0.4rem 1rem",
                    borderRadius: 20,
                    border: `1px solid ${streamProgress != null ? "#00e5ff" : ACCENT}`,
                    background: streamProgress != null ? "rgba(0,229,255,0.12)" : "rgba(103,58,183,0.15)",
                    color: streamProgress != null ? "#00e5ff" : "#b39ddb",
                    font: "600 11px/1 monospace",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    cursor: streamProgress != null ? "default" : "pointer",
                  }}
                >
                  {streamProgress == null
                    ? `⇭ Load full detail (${objectFullPoints.toLocaleString()})`
                    : streamProgress >= 1
                    ? "✓ Full detail loaded"
                    : `Streaming ${Math.round(streamProgress * 100)}%`}
                </button>
              )}
            </div>

            <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end", marginTop: "1.25rem" }}>
              <button
                disabled={confirmLoading}
                onClick={() => {
                  setPreviewModalOpen(false);
                  resetStream();
                  resetMesh();
                  setScenePly(null);
                  setObjectPly(null);
                  setCaptureName("");
                }}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "transparent",
                  border: "2px solid #6c757d",
                  borderRadius: 8,
                  color: "#6c757d",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Discard
              </button>
              <button
                disabled={confirmLoading}
                onClick={async () => {
                  if (confirmLoading) return;
                  setConfirmLoading(true);
                  const nameToSave = captureName || "untitled";
                  try {
                    // Confirm/save is infrequent; convert the binary clouds to
                    // base64 here so Hive's S3 upload path stays unchanged.
                    await confirmStereoCapture(
                      scenePly ? arrayBufferToBase64(scenePly) : null,
                      objectPly ? arrayBufferToBase64(objectPly) : null,
                      capturedMetrics,
                      deviceId,
                      settings?.defaultDataset ?? null,
                      nameToSave
                    );
                    console.log("3D model saved successfully");
                  } catch (error) {
                    console.error("Failed to save 3D model:", error);
                  } finally {
                    setConfirmLoading(false);
                    setPreviewModalOpen(false);
                    setScenePly(null);
                    setObjectPly(null);
                    setCaptureName("");
                  }
                }}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "linear-gradient(135deg, rgba(103, 58, 183, 0.4) 0%, rgba(103, 58, 183, 0.2) 100%)",
                  border: `2px solid ${ACCENT}`,
                  borderRadius: 8,
                  color: ACCENT,
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  cursor: confirmLoading ? "wait" : "pointer",
                  boxShadow: "0 0 20px rgba(103, 58, 183, 0.4)",
                }}
              >
                {confirmLoading ? "SAVING..." : "💾 SAVE 3D MODEL"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  ) : (
    <LoadingSpinner />
  );
};

export default Stream;
